import { Input, Select, SelectItem, Textarea } from "@heroui/react";
import { CalendarBlank } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { useCallback, useMemo, useState } from "react";
import {
  PaymentPlanFrequency,
  PAYMENT_PLAN_FREQUENCY_LABELS,
} from "shared/types/payment-plan";
import { applyMask } from "shared/utils/masks";

interface CreatePaymentPlanFormProps {
  openstandAmount: number;
  onSubmit: (data: {
    totalPlanAmount: number;
    numberOfInstallments: number;
    installmentAmount: number;
    frequency: PaymentPlanFrequency;
    startDate: string;
    remarks?: string;
  }) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

const FREQUENCY_OPTIONS = Object.entries(PAYMENT_PLAN_FREQUENCY_LABELS).map(
  ([key, label]) => ({ key, label })
);

export const CreatePaymentPlanForm = ({
  openstandAmount,
  onSubmit,
  onCancel,
  isSaving,
}: CreatePaymentPlanFormProps) => {
  const [totalPlanAmount, setTotalPlanAmount] = useState(
    openstandAmount.toFixed(2)
  );
  const [numberOfInstallments, setNumberOfInstallments] = useState("3");
  const [frequency, setFrequency] = useState<PaymentPlanFrequency>(
    PaymentPlanFrequency.MONTHLY
  );
  const [startDate, setStartDate] = useState("");
  const [remarks, setRemarks] = useState("");

  const parsedTotal = parseFloat(totalPlanAmount) || 0;
  const parsedCount = parseInt(numberOfInstallments) || 0;

  const installmentAmount = useMemo(() => {
    if (parsedCount <= 0 || parsedTotal <= 0) return 0;
    return Math.floor((parsedTotal / parsedCount) * 100) / 100;
  }, [parsedTotal, parsedCount]);

  const lastInstallmentAmount = useMemo(() => {
    if (parsedCount <= 1) return parsedTotal;
    return Number((parsedTotal - installmentAmount * (parsedCount - 1)).toFixed(2));
  }, [parsedTotal, parsedCount, installmentAmount]);

  // Generate preview dates
  const previewDates = useMemo(() => {
    if (!startDate || parsedCount <= 0) return [];

    const dates: { number: number; date: string; amount: number }[] = [];
    for (let i = 0; i < parsedCount; i++) {
      const d = new Date(startDate);
      switch (frequency) {
        case PaymentPlanFrequency.WEEKLY:
          d.setDate(d.getDate() + i * 7);
          break;
        case PaymentPlanFrequency.BIWEEKLY:
          d.setDate(d.getDate() + i * 14);
          break;
        case PaymentPlanFrequency.MONTHLY:
          d.setMonth(d.getMonth() + i);
          break;
      }

      dates.push({
        number: i + 1,
        date: d.toLocaleDateString("nl-BE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }),
        amount: i === parsedCount - 1 ? lastInstallmentAmount : installmentAmount,
      });
    }
    return dates;
  }, [startDate, parsedCount, frequency, installmentAmount, lastInstallmentAmount]);

  // Auto-calculated end date (last installment date)
  const endDate = useMemo(() => {
    if (!startDate || parsedCount <= 0) return null;
    const d = new Date(startDate);
    const offset = parsedCount - 1;
    switch (frequency) {
      case PaymentPlanFrequency.WEEKLY:
        d.setDate(d.getDate() + offset * 7);
        break;
      case PaymentPlanFrequency.BIWEEKLY:
        d.setDate(d.getDate() + offset * 14);
        break;
      case PaymentPlanFrequency.MONTHLY:
        d.setMonth(d.getMonth() + offset);
        break;
    }
    return d.toLocaleDateString("nl-BE", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }, [startDate, parsedCount, frequency]);

  const isValid = parsedTotal > 0 && parsedCount > 0 && startDate !== "";

  const handleSubmit = useCallback(async () => {
    if (!isValid) return;
    await onSubmit({
      totalPlanAmount: parsedTotal,
      numberOfInstallments: parsedCount,
      installmentAmount,
      frequency,
      startDate,
      remarks: remarks || undefined,
    });
  }, [isValid, parsedTotal, parsedCount, installmentAmount, frequency, startDate, remarks, onSubmit]);

  return (
    <div className="border rounded-lg p-4 bg-neutral-50">
      <h5 className="text-sm font-medium mb-3">Nieuw afbetalingsplan</h5>

      <div className="flex flex-col gap-3">
        {/* Openstand snapshot (read-only) */}
        <div className="flex items-center justify-between p-2 bg-status-bad/10 rounded">
          <span className="text-xs text-neutral-secondary-mid">
            Openstand (snapshot)
          </span>
          <span className="text-sm font-semibold text-status-bad">
            {applyMask(openstandAmount, "valuta")}
          </span>
        </div>

        {/* Total plan amount + number of installments */}
        <div className="grid grid-cols-2 gap-2">
          <Input
            label="Totaal af te betalen"
            type="number"
            step="0.01"
            min="0"
            value={totalPlanAmount}
            onValueChange={setTotalPlanAmount}
            size="sm"
            startContent={
              <span className="text-xs text-neutral-secondary-mid">€</span>
            }
          />
          <Input
            label="Aantal termijnen"
            type="number"
            min="1"
            max="120"
            value={numberOfInstallments}
            onValueChange={setNumberOfInstallments}
            size="sm"
          />
        </div>

        {/* Calculated installment amount (read-only) */}
        <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
          <span className="text-xs text-neutral-secondary-mid">
            Bedrag per termijn
          </span>
          <span className="text-sm font-semibold text-blue-800">
            {applyMask(installmentAmount, "valuta")}
          </span>
        </div>

        {/* Frequency + start date */}
        <div className="grid grid-cols-2 gap-2">
          <Select
            label="Frequentie"
            selectedKeys={[frequency]}
            onSelectionChange={(keys) => {
              const key = Array.from(keys)[0] as PaymentPlanFrequency;
              if (key) setFrequency(key);
            }}
            size="sm"
          >
            {FREQUENCY_OPTIONS.map((option) => (
              <SelectItem key={option.key}>{option.label}</SelectItem>
            ))}
          </Select>

          <Input
            label="Startdatum"
            type="date"
            value={startDate}
            onValueChange={setStartDate}
            size="sm"
          />
        </div>

        {/* Auto-calculated end date */}
        {endDate && (
          <div className="flex items-center justify-between p-2.5 bg-moss-primary/10 border border-moss-primary/20 rounded">
            <div className="flex items-center gap-1.5">
              <CalendarBlank size={14} weight="duotone" className="text-moss-primary" />
              <span className="text-xs font-medium text-moss-primary">
                Einddatum
              </span>
            </div>
            <span className="text-sm font-semibold text-moss-primary">
              {endDate}
            </span>
          </div>
        )}

        {/* Remarks */}
        <Textarea
          label="Opmerkingen"
          placeholder="Optionele opmerkingen..."
          value={remarks}
          onValueChange={setRemarks}
          size="sm"
          minRows={2}
        />

        {/* Preview of installment dates */}
        {previewDates.length > 0 && (
          <div className="border rounded p-2 bg-white">
            <h6 className="text-xs font-medium text-neutral-secondary-mid mb-1.5 flex items-center gap-1">
              <CalendarBlank size={12} />
              Overzicht termijnen
            </h6>
            <div className="flex flex-col gap-0.5 max-h-[160px] overflow-y-auto">
              {previewDates.map((item) => (
                <div
                  key={item.number}
                  className="flex items-center justify-between text-xs py-0.5 px-1 rounded hover:bg-neutral-50"
                >
                  <span className="text-neutral-secondary-mid">
                    Termijn {item.number} — {item.date}
                  </span>
                  <span className="font-medium">
                    {applyMask(item.amount, "valuta")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <ButtonNew
            variant="secondaryNeutral"
            label="Annuleren"
            onClick={onCancel}
            isDisabled={isSaving}
          />
          <ButtonNew
            variant="primaryMoss"
            label={isSaving ? "Opslaan..." : "Opslaan"}
            onClick={handleSubmit}
            isDisabled={!isValid || isSaving}
          />
        </div>
      </div>
    </div>
  );
};

export default CreatePaymentPlanForm;
